// src/reports/RecommendationsByFunction.tsx
import * as React from "react";
import { getRecommendations, type FunctionKey, type MaturityLabel } from "../lib/recommendationMap";

export type RawResponse = {
  question_key?: string; questionKey?: string; key?: string;
  title?: string;
  question_text?: string;
  answer_label?: string; answerLabel?: string; selectedLabel?: string; maturityLabel?: string;
  nist_function?: string | null;
  control_codes?: string[];
};

const ORDER: FunctionKey[] = ["Govern", "Identify", "Protect", "Detect", "Respond", "Recover"];
const COLOR: Record<FunctionKey, string> = {
  Govern:   "bg-purple-600 text-white",
  Identify: "bg-sky-600 text-white",
  Protect:  "bg-emerald-600 text-white",
  Detect:   "bg-amber-600 text-white",
  Respond:  "bg-rose-600 text-white",
  Recover:  "bg-indigo-600 text-white",
};

function fnFromKey(key?: string): FunctionKey | null {
  if (!key) return null;
  const p = key.slice(0, 2).toUpperCase();
  if (p === "GV") return "Govern";
  if (p === "ID") return "Identify";
  if (p === "PR") return "Protect";
  if (p === "DE") return "Detect";
  if (p === "RS") return "Respond";
  if (p === "RC") return "Recover";
  return null;
}
function normLabel(s?: string): MaturityLabel | null {
  const map: Record<string, MaturityLabel> = {
    "Nonexistent": "Nonexistent",
    "Initial": "Initial",
    "Developing": "Developing",
    "Defined/Managed": "Defined/Managed",
    "Optimized": "Optimized",
  };
  return s ? map[s] ?? null : null;
}
const FALLBACK_LABEL: MaturityLabel = "Developing";

type Norm = {
  fn: FunctionKey | null;
  title: string;
  label: MaturityLabel;
  code: string; // question_key
  control_codes: string[];
};

function normalize(responses: RawResponse[]): Norm[] {
  return responses.map((r) => {
    // Phase 2A closeout: never derive a visible question code from array index.
    const code = r.question_key ?? r.questionKey ?? r.key ?? "";

    // Prefer explicit nist_function from DB, fallback to prefix-based detection
    const fn = (r.nist_function as FunctionKey | null) || fnFromKey(code);
    const label = normLabel(r.answer_label ?? r.answerLabel ?? r.selectedLabel ?? r.maturityLabel) ?? FALLBACK_LABEL;
    return {
      fn,
      title: r.title ?? "Untitled",
      label,
      code,
      control_codes: r.control_codes ?? [],
    };
  });
}

/**
 * Get recommendations for a question, preferring per-question DB recs over function-level fallback.
 * For AI questionnaires (isAiLlm=true), NO fallback is allowed — errors are shown instead.
 */
function getRecsForQuestion(
  questionKey: string,
  fn: FunctionKey | null,
  label: MaturityLabel,
  questionRecs: Record<string, Partial<Record<MaturityLabel, string[]>>>,
  isAiLlm: boolean
): string[] {
  // 1) Try per-question overrides from question_recommendations
  let overrides: string[] = [];
  if (label) {
    const perQuestion = questionRecs[questionKey];
    if (perQuestion && perQuestion[label]) {
      overrides = perQuestion[label] || [];
    }
  }

  if (overrides.length > 0) return overrides.slice(0, 4);

  // For AI questionnaires: no fallback allowed
  if (isAiLlm) {
    console.error(`[RecommendationsByFunction] FATAL: No DB recommendations for AI question ${questionKey}, level=${label}`);
    return [`⚠ ERROR: Missing recommendation data for ${questionKey} at "${label}". Contact administrator.`];
  }

  // 2) Fallback to function-level recommendations (non-AI only)
  const baseRecs =
    fn && label ? getRecommendations(fn as FunctionKey, label as MaturityLabel) : [];
  return baseRecs.slice(0, 4);
}

interface Props {
  responses: RawResponse[];
  questionRecs?: Record<string, Partial<Record<MaturityLabel, string[]>>>;
  isAiLlm?: boolean;
}

export function RecommendationsByFunction({ responses, questionRecs = {}, isAiLlm = false }: Props) {
  const items = React.useMemo(() => normalize(responses), [responses]);

  // group by function (unknowns at end)
  const groups = React.useMemo(() => {
    const map = new Map<FunctionKey | "Unknown", Norm[]>();
    ORDER.forEach(fn => map.set(fn, []));
    map.set("Unknown", []);
    items.forEach(it => {
      const k = (it.fn ?? "Unknown") as FunctionKey | "Unknown";
      map.set(k, [...(map.get(k) ?? []), it]);
    });
    return map;
  }, [items]);

  return (
    <section className="print-unclipped print-auto-height">
      <h2 className="text-xl font-semibold">Recommendations &amp; Notes</h2>

      <div className="mt-6 space-y-8">
        {ORDER.map(fn => {
          const bucket = groups.get(fn) ?? [];
          if (!bucket.length) return null;

          return (
            <div key={fn} className="avoid-break">
              {/* colored function header */}
              <div className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${COLOR[fn]} print-force-bg`}>
                {fn}
              </div>

              <div className="mt-3 space-y-4">
                {bucket.map((r, idx) => {
                  const recs = getRecsForQuestion(r.code, fn, r.label, questionRecs, isAiLlm);
                  return (
                    <div key={`${r.code}-${idx}`} className="border rounded-lg p-3 print-card-reset">
                      <div className="text-sm font-medium">{r.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Maturity: {r.label} • Function: {fn}
                        {r.control_codes.length > 0 && (
                          <> • Controls: {r.control_codes.join(" · ")}</>
                        )}
                      </div>
                      <ul className="list-disc pl-5 mt-2 space-y-1 text-sm">
                        {recs.map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Unknown (rare) */}
        {(groups.get("Unknown")?.length ?? 0) > 0 && (
          <div className="avoid-break">
            <div className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium bg-slate-600 text-white print-force-bg">
              Unknown Function
            </div>
            <div className="mt-3 space-y-4">
              {(groups.get("Unknown") ?? []).map((r, idx) => {
                const recs = getRecsForQuestion(r.code, null, r.label, questionRecs, isAiLlm);
                return (
                  <div key={`${r.code}-${idx}`} className="border rounded-lg p-3 print-card-reset">
                    <div className="text-sm font-medium">{r.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Maturity: {r.label} • Function: Unknown
                    </div>
                    <ul className="list-disc pl-5 mt-2 space-y-1 text-sm">
                      {recs.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
